import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import Spinner from 'layout/common/Spinner'
import {PageTitle} from 'layout/master-layout'
import NotificationCard from './components/NotificationCard'
import NotificationSection from './components/NotificationSection'
import EmptyNotification from './components/EmptyNotification'
import {NotificationItem} from 'models/Notification'

import {
 getNotificationsApi,
 markNotificationReadApi,
} from 'utils/api/auth/notificationService'
import KTPagination from 'helpers/ui-components/KTPagination'
import {useNavigate} from 'react-router-dom'

export default function NotificationsPage() {
 const {t} = useTranslation()
 const navigate = useNavigate()
 const [page, setPage] = useState(1)
 const [totalCount, setTotalCount] = useState(0)
 const [pageSize, setPageSize] = useState(10)
 const [unreadCount, setUnreadCount] = useState(0)
 const [loading, setLoading] = useState(false)
 const [notifications, setNotifications] = useState<
  NotificationItem[]
 >([])

 const fetchUnreadCount = async () => {
  const res = await getNotificationsApi({
   app_id: 'web',
   status: 'unread',
   days: 30,
   page: 1,
   page_size: 10,
  })

  if (res) {
   setUnreadCount(res.count)
  }
 }

 const fetchNotifications = async (currentPage = 1) => {
  setLoading(true)

  const res = await getNotificationsApi({
   app_id: 'web',
   status: 'all',
   days: 30,
   page: currentPage,
   page_size: pageSize,
  })

  if (res) {
   setNotifications(res.results)
   setTotalCount(res.count)
  }

  setLoading(false)
 }

 const handleNotificationAction = async (
  item: NotificationItem
 ) => {
  if (!item.deep_link) return

  if (!item.is_read) {
   void markNotificationReadApi({
    app_code: 'web',
    notification_ids: [item.id],
   })
  }

  navigate(`/${item.deep_link}`)
 }

 type GroupKey =
  | 'Today'
  | 'Yesterday'
  | 'Last 7 Days'
  | 'Last 30 Days'
  | 'Earlier'

 const getGroupKey = (dateString: string): GroupKey => {
  const now = new Date()
  const date = new Date(dateString)

  const today = new Date(
   now.getFullYear(),
   now.getMonth(),
   now.getDate()
  )

  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  const sevenDaysAgo = new Date(today)
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

  const thirtyDaysAgo = new Date(today)
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

  const itemDate = new Date(
   date.getFullYear(),
   date.getMonth(),
   date.getDate()
  )

  if (itemDate.getTime() === today.getTime()) {
   return 'Today'
  }

  if (itemDate.getTime() === yesterday.getTime()) {
   return 'Yesterday'
  }

  if (itemDate >= sevenDaysAgo) {
   return 'Last 7 Days'
  }

  if (itemDate >= thirtyDaysAgo) {
   return 'Last 30 Days'
  }

  return 'Earlier'
 }

 const todayNotifications = notifications.filter(
  (item) => getGroupKey(item.created_at) === 'Today'
 )

 const yesterdayNotifications = notifications.filter(
  (item) => getGroupKey(item.created_at) === 'Yesterday'
 )

 const last7DaysNotifications = notifications.filter(
  (item) => getGroupKey(item.created_at) === 'Last 7 Days'
 )

 const last30DaysNotifications = notifications.filter(
  (item) => getGroupKey(item.created_at) === 'Last 30 Days'
 )

 const earlierNotifications = notifications.filter(
  (item) => getGroupKey(item.created_at) === 'Earlier'
 )

 const handleMarkAllRead = async (unreadCount: number) => {
  if (unreadCount === 0) return
  const success = await markNotificationReadApi({
   app_code: 'web',
   mark_all_read: true,
  })

  if (success) {
   void fetchNotifications(page)
   setUnreadCount(0)
  }
 }

 useEffect(() => {
  void fetchUnreadCount()
 }, [])

 useEffect(() => {
  void fetchNotifications(page)
  void fetchUnreadCount()
 }, [page, pageSize])

 return (
  <div className='container relative'>
   <div className='px-[72px] pt-6 pb-10 bg-white w-full min-h-full'>
    <PageTitle
     breadcrumbContainerClass='flex items-center pb-4 mb-4 border-b border-[#E6EBF0] text-[#5A6066] text-sm font-normal'
     breadcrumbs={[
      {
       title: t('breadCrumbs.home'),
       path: '/home-page',
       isActive: false,
      },
      {
       title: 'Notifications',
       path: '/notifications',
       isActive: true,
      },
     ]}
     description='Notifications'
    >
     Notifications
    </PageTitle>

    <div className='flex items-center justify-between mb-8'>
     <div className='text-[#5E6278] text-[14px]'>
      {totalCount} {t('notification.title')}
      {unreadCount > 0 && (
       <>
        {' • '}
        <span className='text-[#CC293F]'>
         {unreadCount} {t('notification.unread')}
        </span>
       </>
      )}
     </div>

     <button
      onClick={async () => handleMarkAllRead(unreadCount)}
      className={`text-[14px] font-medium ${
       unreadCount === 0
        ? 'text-[#98A2B3] cursor-not-allowed'
        : 'text-[#4E83E4] cursor-pointer'
      }`}
     >
      {t('notification.markAllRead')}
     </button>
    </div>

    {loading ? (
     <Spinner />
    ) : notifications.length === 0 ? (
     <EmptyNotification />
    ) : (
     <>
      {!!todayNotifications.length && (
       <NotificationSection title='Today'>
        {todayNotifications.map((item) => (
         <NotificationCard
          key={item.id}
          notification={item}
          onTakeAction={handleNotificationAction}
         />
        ))}
       </NotificationSection>
      )}

      {!!yesterdayNotifications.length && (
       <NotificationSection title='Yesterday'>
        {yesterdayNotifications.map((item) => (
         <NotificationCard
          key={item.id}
          notification={item}
          onTakeAction={handleNotificationAction}
         />
        ))}
       </NotificationSection>
      )}

      {!!last7DaysNotifications.length && (
       <NotificationSection title='Last 7 Days'>
        {last7DaysNotifications.map((item) => (
         <NotificationCard
          key={item.id}
          notification={item}
          onTakeAction={handleNotificationAction}
         />
        ))}
       </NotificationSection>
      )}

      {!!last30DaysNotifications.length && (
       <NotificationSection title='Last 30 Days'>
        {last30DaysNotifications.map((item) => (
         <NotificationCard
          key={item.id}
          notification={item}
          onTakeAction={handleNotificationAction}
         />
        ))}
       </NotificationSection>
      )}

      {!!earlierNotifications.length && (
       <NotificationSection title='Earlier'>
        {earlierNotifications.map((item) => (
         <NotificationCard
          key={item.id}
          notification={item}
          onTakeAction={handleNotificationAction}
         />
        ))}
       </NotificationSection>
      )}
      {totalCount > 0 && (
       <div className='mt-4 flex flex-col'>
        <KTPagination
         currentPage={page}
         itemsPerPage={pageSize}
         totalRecord={totalCount}
         onPageChange={(newPage) => {
          setPage(newPage)
         }}
         setItemsPerPage={(size) => {
          setPageSize(size)
          setPage(1)
         }}
        />
       </div>
      )}
     </>
    )}
   </div>
  </div>
 )
}
